import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { requestPasswordReset } from '@/services/auth';
import Button from '@/components/ui/Button';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const submitLock = useRef(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitLock.current) return;
    setError('');
    submitLock.current = true;
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      submitLock.current = false;
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-600">
            <Stethoscope className="h-5 w-5 text-white" />
          </div>
          <span className="text-[20px] font-bold tracking-tight text-gray-900">Cervica</span>
        </div>

        {sent ? (
          /* Success state */
          <div>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="text-[24px] font-bold text-gray-900">Check your email</h1>
            <p className="mt-2 text-[14px] leading-relaxed text-gray-500">
              If an account exists for <strong className="text-gray-700">{email}</strong>, we sent a password reset link. It expires in 1 hour.
            </p>
            <p className="mt-4 text-[13px] text-gray-400">
              Didn't receive it? Check your spam folder or{' '}
              <button
                onClick={() => { setSent(false); setError(''); }}
                className="font-medium text-primary-600 hover:text-primary-700"
              >
                try again
              </button>.
            </p>
            <Link
              to="/login"
              className="mt-6 flex items-center gap-1.5 text-[13px] font-medium text-primary-600 hover:text-primary-700"
            >
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          /* Form state */
          <div>
            <h1 className="text-[24px] font-bold text-gray-900">Forgot your password?</h1>
            <p className="mt-1 text-[14px] text-gray-500">
              Enter your email and we'll send you a reset link.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-[11px] font-bold text-red-600">!</span>
                  {error}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">
                  Email address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    placeholder="you@practice.com"
                    className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pl-10 text-[14px] text-gray-900 shadow-sm transition-all placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  />
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full rounded-xl py-2.5 text-[14px]"
                isLoading={loading}
              >
                Send reset link
              </Button>
            </form>

            <Link
              to="/login"
              className="mt-6 flex items-center gap-1.5 text-[13px] font-medium text-primary-600 hover:text-primary-700"
            >
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        )}

        <p className="mt-8 text-center text-[11px] text-gray-400">
          Protected by 256-bit encryption &middot; HIPAA Compliant
        </p>
      </div>
    </div>
  );
}
